import { Fragment } from "react"

import { LoadingOutlined } from "@ant-design/icons"
import { Alert, Flex, Form, Input, Space, Spin, Tooltip, Typography } from "antd"
import { useTranslation } from "react-i18next"

import { useIncognito } from "../../hooks/useIncognito"
import Icon from "../../Icon"
import AppUtils, { paymentProcessorMap } from "../../Utils/utils"
import ButtonComponent from "../Button"
import InputComponent from "../Input"

const { Text } = Typography

const spaceVertical = ["chargebee", "next"]

function ConnectPaymentProcessorForm({
  fields,
  onClose,
  onSubmit,
  error,
  extraBtn,
  disabled = false,
  service_name,
  isMobile = false
}) {
  const { t } = useTranslation()
  const incognito = useIncognito()

  const [form] = Form.useForm()

  const onFinish = (value) => {
    const data = { ...value }
    onSubmit(data)
  }
  const handleClose = () => {
    clearFormData()
    onClose()
  }

  function clearFormData() {
    form.resetFields()
  }

  function handleClearError(e, field) {
    form.setFields([
      {
        name: field,
        errors: []
      }
    ])
  }

  function onFocusHandler(e) {
    handleClearError(e, e.target.name)
  }

  const validateNotOnlyWhitespace = (_, value) => {
    if (!value || !value.trim()) {
      return Promise.reject(new Error(t("Fill in all required fields")))
    }
    return Promise.resolve()
  }

  return (
    <Form
      className={"with-default-error"}
      onFinish={onFinish}
      disabled={disabled}
      validateTrigger="onSubmit"
      layout="vertical"
      form={form}
      requiredMark={true}
    >
      {fields.map((field) => (
        <Fragment key={field.id || field}>
          <Form.Item
            //key={field}
            label={field?.label || field}
            name={field?.id || field}
            rules={[
              {
                required: true,
                message: ""
              },
              { validator: validateNotOnlyWhitespace },
              ...(field?.rules || [])
            ]}
            style={{ marginBottom: "16px" }}
            normalize={(value) => value?.trim() || ""}
            tooltip={
              field?.tooltip
                ? {
                    title: field?.tooltip,
                    icon: (
                      <div>
                        <Icon type={"circle_info"} style={{ fontSize: 14 }} />
                      </div>
                    )
                  }
                : null
            }
          >
            {field?.["is_secure"] || incognito ? (
              <Input.Password
                name={field?.id || field}
                placeholder={field?.description?.placeholder || null}
                autoComplete="new-password"
                onFocus={onFocusHandler}
                // Non-secure fields still get the reveal-eye when Incognito forces masking, so
                // typing isn't purely blind - is_secure fields never had one, keep it that way.
                visibilityToggle={!!field?.["is_secure"]}
              />
            ) : (
              <InputComponent
                name={field?.id || field}
                placeholder={field?.description?.placeholder || null}
                autoComplete="new-password"
                onFocus={onFocusHandler}
              />
            )}
          </Form.Item>

          {field?.description?.text && (
            <Form.Item noStyle shouldUpdate={false} dependencies={null}>
              <div style={{ margin: "-8px 0 16px" }}>
                <Space size={4}>
                  <Text>
                    <Space size={0} direction={spaceVertical.includes(service_name) && "vertical"}>
                      <Text className="text-with-icon">
                        <span>{AppUtils.makeBoldText(field?.description?.text, "secondary")}</span>
                        {field?.description?.tooltip ? (
                          <Tooltip
                            placement={isMobile ? "top" : "topLeft"}
                            arrow={false}
                            title={
                              <span>{AppUtils.makeBoldTextHtml(field?.description?.tooltip)}</span>
                            }
                          >
                            <div style={{ marginLeft: "2px" }}>
                              <Icon type="circle_info" style={{ color: "#7A7A83" }} />
                            </div>
                          </Tooltip>
                        ) : null}
                      </Text>
                      <Text>{AppUtils.makeBoldText(field?.description?.mark, "secondary")}</Text>
                    </Space>
                  </Text>
                </Space>
              </div>
            </Form.Item>
          )}
        </Fragment>
      ))}
      {error && (
        <Alert
          type="error"
          message={<Text strong>{t("Payment processor connection failed")}</Text>}
          description={<Text>{error}</Text>}
          showIcon
          icon={<Icon type="alert_triangle_error" />}
          style={{ marginBottom: 16 }}
        />
      )}

      {isMobile ? (
        <Flex vertical gap={8} style={{ padding: "0 3px 3px 0" }}>
          <Flex gap={12}>
            {extraBtn && <div style={{ flex: 1 }}>{extraBtn}</div>}
            <ButtonComponent
              size="large"
              htmlType="button"
              style={{ flex: 1 }}
              onClick={handleClose}
            >
              {t("Cancel")}
            </ButtonComponent>
          </Flex>
          <ButtonComponent type="primary" size="large" htmlType="submit" style={{ width: "100%" }}>
            {disabled ? (
              <Spin
                size="large"
                style={{
                  width: 50,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
                indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
              />
            ) : service_name === "slack" ? (
              t("Connect")
            ) : (
              t("Connect _namePayment_", { namePayment: paymentProcessorMap[service_name] ?? "" })
            )}
          </ButtonComponent>
        </Flex>
      ) : (
        <Flex gap={16} justify="end" style={{ padding: "0 3px 3px 0" }}>
          {extraBtn}
          <ButtonComponent size="large" htmlType="button" onClick={handleClose}>
            {t("Cancel")}
          </ButtonComponent>

          <ButtonComponent type="primary" size="large" htmlType="submit">
            {disabled ? (
              <Spin
                size="large"
                style={{
                  width: 50,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
                indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
              />
            ) : service_name === "slack" ? (
              t("Connect")
            ) : (
              t("Connect _namePayment_", { namePayment: paymentProcessorMap[service_name] ?? "" })
            )}
          </ButtonComponent>
        </Flex>
      )}
    </Form>
  )
}

export default ConnectPaymentProcessorForm
